import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePageTracking } from "./hooks/usePageTracking";
import { motion } from "framer-motion";
import {
    Check, X, Zap, TrendingUp, Shield, ArrowRight, Menu, Package,
    Truck, BarChart2, Warehouse, RefreshCw, MessageSquare, Bot,
    FileText, ChevronDown, ChevronUp
} from "lucide-react";
import logo from "./assets/imgiconlogo.png";
import axios from "axios";
import { API_BASE_URL } from "./utils/api";

const PLANS = [
    {
        id: "starter",
        name: "TinyGlobal Starter",
        tagline: "Everything you need to get moving",
        priceMin: 99,
        priceMax: 149,
        color: "border-slate-200",
        accent: "bg-slate-900",
        badge: null,
        userLimit: 3,
        features: [
            { label: "Shipment tracking & visibility", icon: <Truck size={15}/> },
            { label: "Supplier portal & ASN management", icon: <Package size={15}/> },
            { label: "Inventory visibility (3PL connected)", icon: <Package size={15}/> },
            { label: "Support ticket system", icon: <MessageSquare size={15}/> },
            { label: "AI logistics assistant", icon: <Bot size={15}/> },
            { label: "Customs document management", icon: <FileText size={15}/> },
            { label: "Up to 3 users", icon: <Check size={15}/> },
            { label: "50 customs entries/month", icon: <Check size={15}/> },
        ],
        notIncluded: [
            "Purchase orders",
            "Warehouse management",
            "ATT workflows",
            "Financial dashboards",
            "ERP integrations",
        ],
    },
    {
        id: "growth",
        name: "TinyGlobal Growth",
        tagline: "Scale your operations with confidence",
        priceMin: 249,
        priceMax: 399,
        color: "border-gold-500",
        accent: "bg-gold-500",
        badge: "Most Popular",
        userLimit: 10,
        features: [
            { label: "Everything in Starter", icon: <Check size={15}/>, bold: true },
            { label: "Purchase order management", icon: <FileText size={15}/> },
            { label: "Warehouse management (WMS)", icon: <Warehouse size={15}/> },
            { label: "ATT accreditation workflows", icon: <Shield size={15}/> },
            { label: "Financial dashboards & reports", icon: <BarChart2 size={15}/> },
            { label: "HS code database access", icon: <Check size={15}/> },
            { label: "Order admin & tracking", icon: <Check size={15}/> },
            { label: "Up to 10 users", icon: <Check size={15}/> },
            { label: "200 customs entries/month", icon: <Check size={15}/> },
        ],
        notIncluded: [
            "ERP integrations",
            "Custom workflows",
            "Dedicated onboarding",
        ],
    },
    {
        id: "professional",
        name: "TinyGlobal Professional",
        tagline: "Enterprise-grade logistics intelligence",
        priceMin: 599,
        priceMax: null,
        color: "border-navy-900",
        accent: "bg-navy-900",
        badge: "Full Platform",
        userLimit: -1,
        features: [
            { label: "Everything in Growth", icon: <Check size={15}/>, bold: true },
            { label: "ERP integrations (ERPNext, SAP)", icon: <RefreshCw size={15}/> },
            { label: "Multiple warehouse management", icon: <Warehouse size={15}/> },
            { label: "Advanced analytics & reporting", icon: <TrendingUp size={15}/> },
            { label: "Custom workflow automation", icon: <Zap size={15}/> },
            { label: "Dedicated onboarding specialist", icon: <Check size={15}/> },
            { label: "API partner integrations", icon: <Check size={15}/> },
            { label: "Unlimited users", icon: <Check size={15}/> },
            { label: "Unlimited customs entries", icon: <Check size={15}/> },
        ],
        notIncluded: [],
    },
];

const FAQ = [
    {
        q: "Can I switch plans later?",
        a: "Yes. You can upgrade or downgrade at any time. Upgrades take effect immediately. Downgrades apply at the next billing cycle.",
    },
    {
        q: "What counts as a 'customs entry'?",
        a: "Each import or export declaration submitted through the platform counts as one entry. Drafts do not count until submitted.",
    },
    {
        q: "Do vendors and 3PL partners count as users?",
        a: "No. Vendor and 3PL portal accounts are separate role-based logins and do not count toward your user limit.",
    },
    {
        q: "Is there a setup fee?",
        a: "No setup fees on Starter or Growth. Professional includes a dedicated onboarding session at no extra cost.",
    },
    {
        q: "Do you offer annual billing?",
        a: "Yes — annual billing gives you 2 months free (roughly 17% off). Contact us to set this up.",
    },
];

function FAQItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-slate-100 py-5">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between text-left"
            >
                <span className="font-semibold text-navy-900 text-sm md:text-base">{q}</span>
                {open ? <ChevronUp size={18} className="text-slate-400 shrink-0 ml-4"/> : <ChevronDown size={18} className="text-slate-400 shrink-0 ml-4"/>}
            </button>
            {open && <p className="mt-3 text-slate-600 text-sm leading-relaxed">{a}</p>}
        </div>
    );
}

export default function PricingPage() {
    usePageTracking("/pricing");
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [contactPlan, setContactPlan] = useState(null);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState(null);

    const openContact = (plan) => {
        setContactPlan(plan);
        setContactOpen(true);
    };

    const submitContact = async (e) => {
        e.preventDefault();
        setSending(true);
        setNotice(null);
        const data = Object.fromEntries(new FormData(e.target).entries());
        data.plan = contactPlan?.name || "Unknown";
        try {
            await axios.post(`${API_BASE_URL}/api/demo-request`, data);
            setNotice({ ok: true, msg: "Request received. We'll be in touch within 24 hours." });
            setTimeout(() => { setContactOpen(false); setNotice(null); }, 3000);
        } catch {
            setNotice({ ok: false, msg: "Something went wrong. Please try again." });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans">

            {/* Nav */}
            <nav className="fixed w-full z-50 bg-white/95 backdrop-blur-md shadow-sm py-4">
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-[38px] h-[38px] rounded-full overflow-hidden border border-slate-200">
                            <img src={logo} alt="TinyGlobal" className="w-full h-full object-contain"/>
                        </div>
                        <span className="text-xl font-bold text-navy-900 tracking-tight">TinyGlobal</span>
                    </Link>
                    <div className="hidden md:flex items-center gap-8">
                        <Link to="/#platform" className="text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors">Platform</Link>
                        <Link to="/pricing" className="text-sm font-bold text-navy-900 border-b-2 border-gold-500 pb-0.5">Pricing</Link>
                        <Link to="/login" className="px-5 py-2 border border-navy-900 text-sm font-semibold text-navy-900 hover:bg-navy-900 hover:text-white transition-all rounded-lg">
                            Client Login
                        </Link>
                        <button onClick={() => openContact(PLANS[2])} className="px-6 py-2.5 bg-navy-900 text-white text-sm font-semibold hover:bg-navy-800 transition-colors rounded-lg">
                            Contact Sales
                        </button>
                    </div>
                    <button className="md:hidden text-navy-900" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X size={22}/> : <Menu size={22}/>}
                    </button>
                </div>
            </nav>

            {mobileMenuOpen && (
                <div className="fixed inset-0 z-40 bg-white pt-24 px-6 md:hidden">
                    <div className="flex flex-col gap-6 text-lg font-medium text-navy-900">
                        <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                        <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="text-gold-600 font-bold">Pricing</Link>
                        <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Client Login</Link>
                        <button onClick={() => { openContact(PLANS[2]); setMobileMenuOpen(false); }} className="text-left text-gold-600 font-bold">Contact Sales</button>
                    </div>
                </div>
            )}

            {/* Hero */}
            <section className="pt-32 pb-16 bg-[#071d2e] text-white" style={{ background: 'radial-gradient(circle at 30% 40%, #0b2a42, #071d2e)' }}>
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
                    <span className="inline-block py-1 px-3 border border-gold-500/30 rounded-full text-xs font-bold tracking-wider text-gold-400 mb-6 uppercase bg-gold-500/10">
                        Simple, Transparent Pricing
                    </span>
                    <h1 className="text-4xl md:text-5xl font-extrabold mb-5">
                        Choose Your Plan
                    </h1>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                        Start with what you need. Scale as your supply chain grows. No lock-in, no hidden fees.
                    </p>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
                        {PLANS.map((plan, i) => (
                            <motion.div
                                key={plan.id}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className={`relative bg-white rounded-3xl border-2 shadow-sm flex flex-col overflow-hidden ${plan.color} ${plan.badge === "Most Popular" ? "shadow-gold-500/20 shadow-xl" : ""}`}
                            >
                                {/* Badge */}
                                {plan.badge && (
                                    <div className={`${plan.accent} text-white text-[10px] font-bold uppercase tracking-widest text-center py-2`}>
                                        {plan.badge}
                                    </div>
                                )}

                                <div className="p-8 flex-1 flex flex-col">
                                    {/* Header */}
                                    <div className="mb-8">
                                        <h2 className="text-xl font-bold text-navy-900 mb-1">{plan.name}</h2>
                                        <p className="text-slate-500 text-sm">{plan.tagline}</p>
                                    </div>

                                    {/* Price */}
                                    <div className="mb-8">
                                        <div className="flex items-end gap-1">
                                            <span className="text-4xl font-extrabold text-navy-900">${plan.priceMin}</span>
                                            {plan.priceMax ? (
                                                <span className="text-lg font-bold text-slate-400 mb-1">– ${plan.priceMax}</span>
                                            ) : (
                                                <span className="text-lg font-bold text-slate-400 mb-1">+</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-400 mt-1">per month · billed monthly</p>
                                        {plan.id === "professional" && (
                                            <p className="text-xs text-gold-600 font-semibold mt-1">Custom pricing available for enterprise</p>
                                        )}
                                    </div>

                                    {/* Features */}
                                    <ul className="space-y-3 mb-8 flex-1">
                                        {plan.features.map((f, j) => (
                                            <li key={j} className="flex items-start gap-2.5">
                                                <span className="text-emerald-500 mt-0.5 shrink-0">{f.icon}</span>
                                                <span className={`text-sm ${f.bold ? "font-bold text-navy-900" : "text-slate-700"}`}>{f.label}</span>
                                            </li>
                                        ))}
                                        {plan.notIncluded.map((f, j) => (
                                            <li key={j} className="flex items-start gap-2.5 opacity-40">
                                                <X size={15} className="text-slate-400 mt-0.5 shrink-0"/>
                                                <span className="text-sm text-slate-500 line-through">{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <button
                                        onClick={() => openContact(plan)}
                                        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                                            plan.badge === "Most Popular"
                                                ? "bg-gold-500 text-navy-900 hover:bg-gold-400 shadow-lg shadow-gold-500/20"
                                                : plan.id === "professional"
                                                ? "bg-navy-900 text-white hover:bg-navy-800"
                                                : "bg-slate-100 text-navy-900 hover:bg-slate-200"
                                        }`}
                                    >
                                        Get Started <ArrowRight size={16}/>
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <p className="text-center text-xs text-slate-400 mt-8">
                        All plans include SSL, 99.9% uptime SLA, and free data migration. Annual billing available (save 2 months).
                    </p>
                </div>
            </section>

            {/* Feature Comparison Table */}
            <section className="py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold text-navy-900 mb-3">Compare Plans</h2>
                        <div className="h-1 w-16 bg-gold-500 rounded-full mx-auto"/>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-slate-100">
                                    <th className="text-left py-4 pr-8 text-sm font-bold text-slate-500 uppercase tracking-wider">Feature</th>
                                    <th className="text-center py-4 px-4 text-sm font-bold text-navy-900">Starter</th>
                                    <th className="text-center py-4 px-4 text-sm font-bold text-gold-600">Growth</th>
                                    <th className="text-center py-4 px-4 text-sm font-bold text-navy-900">Professional</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ["Shipment tracking", true, true, true],
                                    ["Supplier portal (ASN)", true, true, true],
                                    ["Inventory visibility", true, true, true],
                                    ["Support tickets", true, true, true],
                                    ["AI logistics assistant", true, true, true],
                                    ["Customs documents", true, true, true],
                                    ["Purchase orders", false, true, true],
                                    ["Warehouse management (WMS)", false, true, true],
                                    ["ATT accreditation workflows", false, true, true],
                                    ["Financial dashboards", false, true, true],
                                    ["HS code database", false, true, true],
                                    ["ERP integrations", false, false, true],
                                    ["Multiple warehouses", false, false, true],
                                    ["Custom workflow automation", false, false, true],
                                    ["Dedicated onboarding", false, false, true],
                                    ["API partner integrations", false, false, true],
                                    ["Users", "Up to 3", "Up to 10", "Unlimited"],
                                    ["Customs entries/month", "50", "200", "Unlimited"],
                                ].map(([label, s, g, p], i) => (
                                    <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? "bg-slate-50/30" : ""}`}>
                                        <td className="py-3.5 pr-8 text-sm text-slate-700 font-medium">{label}</td>
                                        {[s, g, p].map((val, j) => (
                                            <td key={j} className="py-3.5 px-4 text-center">
                                                {val === true ? (
                                                    <Check size={18} className="text-emerald-500 mx-auto"/>
                                                ) : val === false ? (
                                                    <span className="text-slate-200 text-lg">—</span>
                                                ) : (
                                                    <span className="text-xs font-bold text-navy-900">{val}</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold text-navy-900 mb-3">Frequently Asked Questions</h2>
                        <div className="h-1 w-16 bg-gold-500 rounded-full mx-auto"/>
                    </div>
                    <div>
                        {FAQ.map((item, i) => <FAQItem key={i} {...item}/>)}
                    </div>
                </div>
            </section>

            {/* CTA Banner */}
            <section className="py-20 bg-[#071d2e]">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                        Not sure which plan fits?
                    </h2>
                    <p className="text-slate-400 mb-8 text-lg">
                        Talk to us — we'll map your operations to the right tier with no pressure.
                    </p>
                    <button
                        onClick={() => openContact(PLANS[1])}
                        className="px-10 py-4 bg-gold-500 text-navy-900 font-bold rounded-xl hover:bg-gold-400 transition-colors text-base shadow-lg shadow-gold-500/20"
                    >
                        Book a Free Consultation
                    </button>
                </div>
            </section>

            {/* Contact Modal */}
            {contactOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={() => setContactOpen(false)}/>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
                    >
                        <div className="bg-navy-900 px-8 py-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-white font-serif">Get Started — {contactPlan?.name}</h2>
                                <p className="text-slate-400 text-xs mt-0.5">Our team will respond within 24 hours</p>
                            </div>
                            <button onClick={() => setContactOpen(false)} className="text-slate-400 hover:text-white p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                                <X size={20}/>
                            </button>
                        </div>
                        <div className="p-8">
                            <form onSubmit={submitContact} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
                                        <input name="name" required placeholder="John Smith" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none text-sm transition-all"/>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Company</label>
                                        <input name="company" required placeholder="Acme Corp" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none text-sm transition-all"/>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Work Email</label>
                                    <input name="email" type="email" required placeholder="john@company.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none text-sm transition-all"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Message (optional)</label>
                                    <textarea name="message" rows={3} placeholder="Tell us about your logistics needs..." className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none text-sm resize-none transition-all"/>
                                </div>
                                {notice && (
                                    <div className={`px-4 py-3 rounded-xl text-sm font-medium ${notice.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                                        {notice.msg}
                                    </div>
                                )}
                                <div className="flex gap-3 pt-2">
                                    <button type="button" onClick={() => setContactOpen(false)} className="px-5 py-3 text-sm font-semibold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={sending} className="flex-1 py-3 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 transition-colors disabled:opacity-60 text-sm flex items-center justify-center gap-2">
                                        {sending ? "Sending…" : <><ArrowRight size={16}/> Submit Request</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
